import React, { useCallback, useEffect, useRef, useState } from "react";
import { Grid, CircularProgress, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { URL } from "../../common/api/urls";
import useAuth from "../../common/hooks/useAuth";
import useRefreshToken from "../../common/hooks/useRefreshToken";
import NewLoginModal from "../../components/landing/sections/LoginPopupNew";
import { openSessionLoginModal } from "../../common/api/hooks/useAxiosPrivate";
import { axiosPrivate } from "../../common/api/axios";
import AppErrorPopup from "../../components/AppErrorPopup";

const SpecialDemoSubscribeHandler = () => {
  const navigate = useNavigate();
  const refresh = useRefreshToken();
  const { auth } = useAuth();
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDone, setIsDone] = useState(false);
  const [showLoginPopup, setShowLoginPopup] = useState(false);
  const [popupState, setPopupState] = useState({
    open: false,
    message: "",
    severity: "info"
  });
  const hasRequested = useRef(false);
  const hasRedirected = useRef(false);

  useEffect(() => {
    let isMounted = true;
    const checkAuth = async () => {
      if (auth?.accessToken) {
        if (isMounted) setIsCheckingAuth(false);
        return;
      }
      try {
        await refresh();
      } catch (err) {
        if (isMounted) {
          if (err?.response?.status === 410) {
            openSessionLoginModal(err);
          } else {
            setShowLoginPopup(true);
          }
        }
      } finally {
        if (isMounted) setIsCheckingAuth(false);
      }
    };
    checkAuth();
    return () => {
      isMounted = false;
    };
  }, [auth?.accessToken, refresh]);

  const callSpecialSubscribe = useCallback(async (token) => {
    if (hasRequested.current) return;
    hasRequested.current = true;
    setIsSubmitting(true);
    const authToken = token || auth?.accessToken;
    try {
      const response = await axiosPrivate.request({
        method: "GET",
        url: URL.SPECIAL_DEMO_SUBSCRIBE,
        headers: authToken ? { Authorization: `${authToken}` } : undefined
      });
      // Keep auth/session in sync with newly activated plan entitlements.
      try {
        await refresh(true);
      } catch (refreshErr) {
        console.error("Token refresh after special subscribe failed:", refreshErr);
      }
      setPopupState({
        open: true,
        message: response?.data?.message || "Special demo subscription activated successfully.",
        severity: "success"
      });
    } catch (err) {
      console.error("Special demo subscribe request failed:", err);
      setPopupState({
        open: true,
        message: err?.response?.data?.message || "Unable to activate special demo subscription.",
        severity: "error"
      });
    } finally {
      setIsSubmitting(false);
      setIsDone(true);
    }
  }, [auth?.accessToken, refresh]);

  useEffect(() => {
    if (isCheckingAuth || !auth?.accessToken || hasRequested.current) return;
    callSpecialSubscribe(auth?.accessToken);
  }, [auth?.accessToken, callSpecialSubscribe, isCheckingAuth]);

  useEffect(() => {
    if (!isDone || popupState.open || hasRedirected.current) return;
    hasRedirected.current = true;
    navigate("/", { replace: true });
  }, [isDone, navigate, popupState.open]);

  const isLoading = isCheckingAuth || isSubmitting || (auth?.accessToken && !isDone);

  return (
    <>
      <Grid
        component="main"
        container
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        sx={{ height: "calc(100vh - 64px)" }}
      >
        {isLoading ? (
          <>
            <CircularProgress />
            <Typography variant="body1" sx={{ mt: 2 }}>
              Activating special demo subscription...
            </Typography>
          </>
        ) : (
          <Typography variant="body1">Please log in to continue.</Typography>
        )}
      </Grid>
      {showLoginPopup ? (
        <NewLoginModal
          open={true}
          onClose={() => setShowLoginPopup(false)}
          onLoginSuccess={(loginResponse) => {
            setShowLoginPopup(false);
            setIsCheckingAuth(false);
            callSpecialSubscribe(loginResponse?.token);
          }}
        />
      ) : null}
      <AppErrorPopup
        open={popupState.open}
        message={popupState.message}
        severity={popupState.severity}
        onClose={() => setPopupState((prev) => ({ ...prev, open: false }))}
        autoCloseTimer={0}
        showCloseX
      />
    </>
  );
};

export default SpecialDemoSubscribeHandler;
